import { useState, useRef, useEffect, ReactNode } from 'react';
import { whatsappColors } from './types';

interface ResizablePanelProps {
  children: ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}

export function ResizablePanel({ 
  children, 
  defaultWidth = 400, 
  minWidth = 300, 
  maxWidth = 600 
}: ResizablePanelProps) {
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, maxWidth]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  return (
    <div 
      ref={panelRef}
      className="h-full flex flex-col relative"
      style={{ 
        width: `${width}px`,
        backgroundColor: whatsappColors.panelBg,
        borderRight: `1px solid ${whatsappColors.borderDark}`,
      }}
    >
      {children}
      
      {/* Resize Handle */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#00a884] transition-colors z-10"
        style={{
          backgroundColor: isResizing ? whatsappColors.green : 'transparent',
        }}
        onMouseDown={handleMouseDown}
        title="Arraste para redimensionar"
      >
        {/* Visual indicator on hover */}
        <div 
          className="absolute top-1/2 right-0 transform -translate-y-1/2 w-1 h-12 rounded-l-full transition-opacity"
          style={{
            backgroundColor: whatsappColors.green,
            opacity: isResizing ? 1 : 0,
          }}
        />
      </div>
    </div>
  );
}
